import React, { useState } from 'react';
import { motion } from 'framer-motion';

const StrategicActionPlan = ({ data }) => {
  const [activeTab, setActiveTab] = useState('overview');
  if (!data) return null;

  const { overallReadiness = 72, readinessLevel = 'Good', timeToImpact = '3-6 months', estimatedGrowth = '+25%', strategicPillars = [], quickWins = [], milestones = [], riskFactors = [], competitiveActions = [] } = data;

  const getStatusColor = (s) => s === 'On Track' ? '#10b981' : s === 'Needs Work' ? '#f59e0b' : '#ef4444';
  const getPriorityColor = (p) => p === 'High' ? '#ef4444' : p === 'Medium' ? '#f59e0b' : '#10b981';
  const getSeverityBg = (s) => s === 'High' ? 'rgba(239,68,68,0.2)' : s === 'Medium' ? 'rgba(245,158,11,0.2)' : 'rgba(16,185,129,0.2)';
  const getProgressColor = (s) => s === 'Complete' ? '#10b981' : s === 'Active' ? '#6366f1' : '#d1d5db';

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'pillars', label: 'Strategic Pillars' },
    { id: 'execution', label: 'Execution Plan' }
  ];

  const readinessAngle = (overallReadiness / 100) * 360;

  return (
    <div style={{ color: '#e5e7eb' }}>
      <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)} style={{ padding: '8px 20px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 16, fontWeight: 600, background: activeTab === t.id ? 'linear-gradient(135deg, #6366f1, #8b5cf6)' : 'rgba(99,102,241,0.1)', color: activeTab === t.id ? '#fff' : '#6366f1', transition: 'all 0.2s' }}>
            {t.label}
          </button>
        ))}
      </div>

      {activeTab === 'overview' && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 20, marginBottom: 30 }}>
            <div style={{ background: 'rgba(17,24,39,0.7)', borderRadius: 16, padding: 24, textAlign: 'center', border: '1px solid rgba(99,102,241,0.2)' }}>
              <p style={{ color: '#d1d5db', fontSize: 14, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 }}>Readiness Score</p>
              <div style={{ position: 'relative', width: 120, height: 120, margin: '0 auto 12px' }}>
                <svg viewBox="0 0 120 120" style={{ transform: 'rotate(-90deg)' }}>
                  <circle cx="60" cy="60" r="52" fill="none" stroke="rgba(75,85,99,0.3)" strokeWidth="8" />
                  <circle cx="60" cy="60" r="52" fill="none" stroke={overallReadiness >= 70 ? '#10b981' : overallReadiness >= 40 ? '#f59e0b' : '#ef4444'} strokeWidth="8" strokeDasharray={`${(overallReadiness/100)*327} 327`} strokeLinecap="round" />
                </svg>
                <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', textAlign: 'center' }}>
                  <span style={{ fontSize: 30, fontWeight: 700, color: '#fff' }}>{overallReadiness}</span>
                  <span style={{ fontSize: 15, color: '#d1d5db', display: 'block' }}>/ 100</span>
                </div>
              </div>
              <span style={{ color: overallReadiness >= 70 ? '#10b981' : '#f59e0b', fontWeight: 600, fontSize: 16 }}>{readinessLevel}</span>
            </div>

            <div style={{ background: 'rgba(17,24,39,0.7)', borderRadius: 16, padding: 24, border: '1px solid rgba(99,102,241,0.2)' }}>
              <p style={{ color: '#d1d5db', fontSize: 14, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 16 }}>Key Metrics</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: '#d1d5db' }}>Time to Impact</span><span style={{ color: '#6366f1', fontWeight: 600 }}>{timeToImpact}</span></div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: '#d1d5db' }}>Est. Growth</span><span style={{ color: '#10b981', fontWeight: 600 }}>{estimatedGrowth}</span></div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: '#d1d5db' }}>Pillars</span><span style={{ color: '#fff', fontWeight: 600 }}>{strategicPillars.length}</span></div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: '#d1d5db' }}>Total Actions</span><span style={{ color: '#fff', fontWeight: 600 }}>{strategicPillars.reduce((a, p) => a + (p.actions?.length || 0), 0)}</span></div>
              </div>
            </div>

            <div style={{ background: 'rgba(17,24,39,0.7)', borderRadius: 16, padding: 24, border: '1px solid rgba(99,102,241,0.2)' }}>
              <p style={{ color: '#d1d5db', fontSize: 14, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 16 }}>Quick Wins</p>
              {quickWins.slice(0, 4).map((qw, i) => (
                <div key={i} style={{ marginBottom: 10, padding: '8px 12px', background: 'rgba(16,185,129,0.08)', borderRadius: 8, borderLeft: '3px solid #10b981' }}>
                  <p style={{ color: '#fff', fontSize: 15, fontWeight: 500, marginBottom: 2 }}>{qw.action}</p>
                  <p style={{ color: '#d1d5db', fontSize: 13 }}>{qw.timeframe}  {qw.effort} effort</p>
                </div>
              ))}
            </div>
          </div>

          <p style={{ color: '#94a3b8', fontSize: 14, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 }}>Pillar Performance</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 30 }}>
            {strategicPillars.map((p, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }} style={{ background: 'rgba(17,24,39,0.7)', borderRadius: 12, padding: 16, borderTop: `3px solid ${getStatusColor(p.status)}` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <span style={{ color: '#fff', fontWeight: 600, fontSize: 16 }}>{p.name}</span>
                  <span style={{ color: getStatusColor(p.status), fontSize: 24, fontWeight: 700 }}>{p.score}</span>
                </div>
                <div style={{ background: 'rgba(75,85,99,0.3)', borderRadius: 4, height: 6, marginBottom: 8 }}>
                  <div style={{ background: getStatusColor(p.status), borderRadius: 4, height: '100%', width: `${p.score}%`, transition: 'width 0.5s' }} />
                </div>
                <span style={{ background: getSeverityBg(p.status === 'On Track' ? 'Low' : p.status === 'Needs Work' ? 'Medium' : 'High'), color: getStatusColor(p.status), padding: '2px 8px', borderRadius: 10, fontSize: 14, fontWeight: 600 }}>{p.status}</span>
              </motion.div>
            ))}
          </div>

          <p style={{ color: '#94a3b8', fontSize: 14, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12 }}>Risk Assessment</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 12 }}>
            {riskFactors.map((r, i) => (
              <div key={i} style={{ background: getSeverityBg(r.severity), borderRadius: 12, padding: 16, border: `1px solid ${getPriorityColor(r.severity)}33` }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                  <span style={{ color: '#1f2937', fontWeight: 600, fontSize: 16 }}>{r.risk}</span>
                  <span style={{ background: getPriorityColor(r.severity) + '22', color: getPriorityColor(r.severity), padding: '2px 8px', borderRadius: 10, fontSize: 14, fontWeight: 600 }}>{r.severity}</span>
                </div>
                <p style={{ color: '#312e81', fontSize: 14, fontStyle: 'italic' }}>Mitigation: {r.mitigation}</p>
              </div>
            ))}
          </div>
        </motion.div>
      )}

      {activeTab === 'pillars' && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          {strategicPillars.map((pillar, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }} style={{ background: 'rgba(17,24,39,0.7)', borderRadius: 16, padding: 24, marginBottom: 20, border: '1px solid rgba(99,102,241,0.2)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <div>
                  <h3 style={{ color: '#fff', fontSize: 20, fontWeight: 700, marginBottom: 4 }}>{pillar.name}</h3>
                  <p style={{ color: '#d1d5db', fontSize: 15 }}>{pillar.description}</p>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 30, fontWeight: 700, color: getStatusColor(pillar.status) }}>{pillar.score}</div>
                  <span style={{ background: getSeverityBg(pillar.status === 'On Track' ? 'Low' : 'Medium'), color: getStatusColor(pillar.status), padding: '2px 10px', borderRadius: 10, fontSize: 14, fontWeight: 600 }}>{pillar.status}</span>
                </div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {(pillar.actions || []).map((action, j) => (
                  <div key={j} style={{ background: 'rgba(0,0,0,0.2)', borderRadius: 10, padding: 14, borderLeft: `3px solid ${getPriorityColor(action.priority)}` }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                      <span style={{ color: '#fff', fontWeight: 600, fontSize: 16 }}>{action.task}</span>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <span style={{ background: getPriorityColor(action.priority) + '22', color: getPriorityColor(action.priority), padding: '2px 8px', borderRadius: 10, fontSize: 14, fontWeight: 600 }}>{action.priority}</span>
                        <span style={{ background: action.status === 'Complete' ? 'rgba(16,185,129,0.15)' : action.status === 'In Progress' ? 'rgba(99,102,241,0.15)' : 'rgba(75,85,99,0.3)', color: action.status === 'Complete' ? '#10b981' : action.status === 'In Progress' ? '#6366f1' : '#d1d5db', padding: '2px 8px', borderRadius: 10, fontSize: 14, fontWeight: 600 }}>{action.status}</span>
                      </div>
                    </div>
                    <p style={{ color: '#d1d5db', fontSize: 14, marginBottom: 4 }}>{action.details}</p>
                    <div style={{ display: 'flex', gap: 12, fontSize: 13 }}>
                      <span style={{ color: '#6366f1' }}>Timeline: {action.timeline}</span>
                      <span style={{ color: action.impact === 'High' ? '#10b981' : '#f59e0b' }}>Impact: {action.impact}</span>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          ))}
        </motion.div>
      )}

      {activeTab === 'execution' && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <p style={{ color: '#94a3b8', fontSize: 14, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 16 }}>Milestones</p>
          <div style={{ position: 'relative', marginBottom: 40 }}>
            <div style={{ position: 'absolute', left: 16, top: 0, bottom: 0, width: 2, background: 'rgba(99,102,241,0.3)' }} />
            {milestones.map((m, i) => (
              <motion.div key={i} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.15 }} style={{ display: 'flex', gap: 20, marginBottom: 20, paddingLeft: 40, position: 'relative' }}>
                <div style={{ position: 'absolute', left: 9, top: 4, width: 16, height: 16, borderRadius: '50%', background: getProgressColor(m.status), border: '3px solid rgba(17,24,39,0.9)' }} />
                <div style={{ background: 'rgba(17,24,39,0.7)', borderRadius: 12, padding: 16, flex: 1, border: `1px solid ${getProgressColor(m.status)}33` }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                    <span style={{ color: '#fff', fontWeight: 600 }}>{m.title}</span>
                    <span style={{ background: getProgressColor(m.status) + '22', color: getProgressColor(m.status), padding: '2px 8px', borderRadius: 10, fontSize: 14, fontWeight: 600 }}>{m.status}</span>
                  </div>
                  <div style={{ display: 'flex', gap: 16, fontSize: 14 }}>
                    <span style={{ color: '#6366f1' }}>Target: {m.target}</span>
                    <span style={{ color: '#d1d5db' }}>Metrics: {m.metrics}</span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <p style={{ color: '#94a3b8', fontSize: 14, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 16 }}>Competitive Actions</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 16 }}>
            {competitiveActions.map((ca, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }} style={{ background: 'rgba(17,24,39,0.7)', borderRadius: 12, padding: 20, border: '1px solid rgba(99,102,241,0.2)' }}>
                <h4 style={{ color: '#fff', fontWeight: 700, fontSize: 18, marginBottom: 10 }}>{ca.area}</h4>
                <div style={{ marginBottom: 10 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span style={{ color: '#ef4444', fontSize: 14 }}>Current</span>
                    <span style={{ color: '#10b981', fontSize: 14 }}>Target</span>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <div style={{ flex: 1, background: 'rgba(239,68,68,0.1)', borderRadius: 8, padding: 8 }}>
                      <p style={{ color: '#f87171', fontSize: 14 }}>{ca.currentPosition}</p>
                    </div>
                    <div style={{ flex: 1, background: 'rgba(16,185,129,0.1)', borderRadius: 8, padding: 8 }}>
                      <p style={{ color: '#34d399', fontSize: 14 }}>{ca.targetPosition}</p>
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {(ca.actions || []).map((a, j) => (
                    <span key={j} style={{ background: 'rgba(99,102,241,0.1)', color: '#818cf8', padding: '4px 10px', borderRadius: 8, fontSize: 13 }}>{a}</span>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default StrategicActionPlan;
